import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _extends from "@babel/runtime/helpers/esm/extends";
import React from 'react';
import isEqual from 'fast-deep-equal';
import youTubePlayer from 'youtube-player';
import classNames from 'classnames';
import { Spinner } from 'reactstrap';
var defaultPlayerVars = {
  modestbranding: 1,
  rel: 0,
  enablejsapi: 1
};
var youTubeApi;

function shouldUpdateVideo(prevProps, props) {
  // A changing video should always trigger an update
  if (prevProps.videoId !== props.videoId) {
    return true;
  } // Otherwise, a change in the start/end time playerVars also requires a player
  // update.


  var prevVars = prevProps.opts.playerVars || {};
  var vars = props.opts.playerVars || {};
  return prevVars.start !== vars.start || prevVars.end !== vars.end;
}
/**
 * Neutralise API options that only require a video update, leaving only options
 * that require a player reset. The results can then be compared to see if a
 * player reset is necessary.
 */


function filterResetOptions(opts) {
  return _extends({}, opts, {
    playerVars: _extends({}, opts.playerVars, {
      autoplay: 0,
      start: 0,
      end: 0
    })
  });
}
/**
 * Check whether a `props` change should result in the player being reset.
 * The player is reset when the `props.opts` change, except if the only change
 * is in the `start` and `end` playerVars, because a video update can deal with
 * those.
 */


function shouldResetPlayer(prevProps, props) {
  return !isEqual(filterResetOptions(prevProps.opts), filterResetOptions(props.opts));
}
/**
 * Check whether a props change should result in an id or className update.
 */


function shouldUpdatePlayer(prevProps, props) {
  return prevProps.id !== props.id || prevProps.className !== props.className;
}

var YouTubePlayer = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(YouTubePlayer, _React$Component);

  function YouTubePlayer(props) {
    var _this;

    _this = _React$Component.call(this, props) || this;

    _defineProperty(_assertThisInitialized(_this), "onPlayerReady", function (event) {
      if (_this.props.isLazy) {
        if (_this.internalPlayer) {
          _this.internalPlayer.playVideo();
        }

        _this.setState({
          thumbnailHidden: true,
          isPlayerLoading: false
        });
      }

      _this.props.onReady(event);
    });

    _defineProperty(_assertThisInitialized(_this), "onPlayerError", function (event) {
      return _this.props.onError(event);
    });

    _defineProperty(_assertThisInitialized(_this), "onPlayerStateChange", function (event) {
      _this.props.onStateChange(event);

      switch (event.data) {
        case YouTubePlayer.PlayerState.ENDED:
          _this.props.onEnd(event);

          break;

        case YouTubePlayer.PlayerState.PLAYING:
          _this.props.onPlay(event);

          break;

        case YouTubePlayer.PlayerState.PAUSED:
          _this.props.onPause(event);

          break;

        default:
      }
    });

    _defineProperty(_assertThisInitialized(_this), "onPlayerPlaybackRateChange", function (event) {
      return _this.props.onPlaybackRateChange(event);
    });

    _defineProperty(_assertThisInitialized(_this), "onPlayerPlaybackQualityChange", function (event) {
      return _this.props.onPlaybackQualityChange(event);
    });

    _defineProperty(_assertThisInitialized(_this), "playVideo", function () {
      _this.setState({
        isPlayerLoading: true
      });

      _this.createPlayer();
    });

    _defineProperty(_assertThisInitialized(_this), "createPlayer", function () {
      if (typeof document === 'undefined') return;

      var playerVars = _extends({}, defaultPlayerVars, {
        origin: window.location.origin
      }, _this.props.opts.playerVars);

      var playerOpts = _extends({}, _this.props.opts, {
        playerVars: _extends({}, playerVars),
        videoId: _this.props.videoId
      });

      _this.internalPlayer = youTubePlayer(_this.container, playerOpts); // attach event handlers

      _this.internalPlayer.on('ready', _this.onPlayerReady);

      _this.internalPlayer.on('error', _this.onPlayerError);

      _this.internalPlayer.on('stateChange', _this.onPlayerStateChange);

      _this.internalPlayer.on('playbackRateChange', _this.onPlayerPlaybackRateChange);

      _this.internalPlayer.on('playbackQualityChange', _this.onPlayerPlaybackQualityChange);
    });

    _defineProperty(_assertThisInitialized(_this), "resetPlayer", function () {
      return _this.internalPlayer.destroy().then(_this.createPlayer);
    });

    _defineProperty(_assertThisInitialized(_this), "updatePlayer", function () {
      _this.internalPlayer.getIframe().then(function (iframe) {
        if (_this.props.id) iframe.setAttribute('id', _this.props.id);else iframe.removeAttribute('id');
        if (_this.props.className) iframe.setAttribute('class', _this.props.className);else iframe.removeAttribute('class');
      });
    });

    _defineProperty(_assertThisInitialized(_this), "updateVideo", function () {
      if (typeof _this.props.videoId === 'undefined' || _this.props.videoId === null) {
        _this.internalPlayer.stopVideo();

        return;
      } // set queueing options


      var autoplay = false;
      var opts = {
        videoId: _this.props.videoId
      };

      if ('playerVars' in _this.props.opts) {
        autoplay = _this.props.opts.playerVars.autoplay === 1;

        if ('start' in _this.props.opts.playerVars) {
          opts.startSeconds = _this.props.opts.playerVars.start;
        }

        if ('end' in _this.props.opts.playerVars) {
          opts.endSeconds = _this.props.opts.playerVars.end;
        }
      } // if autoplay is enabled loadVideoById


      if (autoplay) {
        _this.internalPlayer.loadVideoById(opts);

        return;
      } // default behaviour just cues the video


      _this.internalPlayer.cueVideoById(opts);
    });

    _defineProperty(_assertThisInitialized(_this), "refContainer", function (container) {
      _this.container = container;
    });

    _this.container = null;
    _this.internalPlayer = null;
    _this.state = {
      thumbnailHidden: false
    };
    return _this;
  }

  var _proto = YouTubePlayer.prototype;

  _proto.componentDidMount = function componentDidMount() {
    if (!this.props.isLazy) {
      this.createPlayer();
    } else {
      if (!youTubeApi) {
        var script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(script);
        youTubeApi = true;
      }
    }
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (shouldUpdatePlayer(prevProps, this.props)) {
      this.updatePlayer();
    }

    if (shouldResetPlayer(prevProps, this.props)) {
      this.resetPlayer();
    }

    if (shouldUpdateVideo(prevProps, this.props)) {
      this.updateVideo();
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    if (this.internalPlayer) {
      this.internalPlayer.destroy();
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    var classes = classNames('youtube-player', 'embed-responsive', this.props.className);
    var containerClasses = classNames('youtube-player__container', 'embed-responsive-item', this.props.containerClassName);
    return /*#__PURE__*/React.createElement("div", {
      id: this.props.id,
      className: classes
    }, /*#__PURE__*/React.createElement("div", {
      className: "youtube-player__spacer",
      style: {
        paddingTop: this.props.aspectRatioHeight / this.props.aspectRatioWidth * 100 + "%"
      }
    }), this.props.isLazy && /*#__PURE__*/React.createElement("div", {
      className: "youtube-player__thumbnail embed-responsive-item " + (this.state.thumbnailHidden ? 'youtube-player__thumbnail--hidden' : ''),
      style: {
        backgroundImage: "url()"
      },
      onClick: function onClick() {
        return _this2.playVideo();
      }
    }, this.props.customThumbnail ? this.props.customThumbnail : /*#__PURE__*/React.createElement("img", {
      className: "youtube-player__thumbnail-image",
      src: "https://i.ytimg.com/vi/" + this.props.videoId + "/" + this.props.thumbnailSize + ".jpg",
      alt: "Thumbnail"
    }), /*#__PURE__*/React.createElement("div", {
      className: "youtube-player__play " + (this.state.isPlayerLoading !== undefined ? 'youtube-player__play--hidden' : '')
    }), /*#__PURE__*/React.createElement("div", {
      className: "youtube-player__spinner " + (this.state.isPlayerLoading ? 'youtube-player__spinner--visible' : '')
    }, /*#__PURE__*/React.createElement(Spinner, {
      color: "white"
    }))), /*#__PURE__*/React.createElement("div", {
      className: containerClasses,
      ref: this.refContainer
    }));
  };

  return YouTubePlayer;
}(React.Component);

_defineProperty(YouTubePlayer, "PlayerState", {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5
});

export { YouTubePlayer as default };
YouTubePlayer.defaultProps = {
  opts: {
    playerVars: {}
  },
  thumbnailSize: 'sddefault',
  aspectRatioWidth: 16,
  aspectRatioHeight: 9,
  onReady: function onReady() {},
  onError: function onError() {},
  onPlay: function onPlay() {},
  onPause: function onPause() {},
  onEnd: function onEnd() {},
  onStateChange: function onStateChange() {},
  onPlaybackRateChange: function onPlaybackRateChange() {},
  onPlaybackQualityChange: function onPlaybackQualityChange() {}
};